import { cn } from "@/lib/utils";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

interface ClientBrandPreviewProps {
  name: string;
  primaryColor?: string | null;
  accentColor?: string | null;
  logoUrl?: string | null;
  className?: string;
  /** Caption under the swatches/logo; defaults to a generic brand preview line. */
  message?: React.ReactNode;
}

/**
 * Small live preview of a client's brand — a logo thumbnail (falling back to
 * two color swatches) plus a caption. Renders nothing if there's no brand to
 * show. Shared between `ClientEditDialog` (previewing draft edits) and the
 * new-project review step (previewing the already-saved client brand).
 */
export function ClientBrandPreview({
  name,
  primaryColor,
  accentColor,
  logoUrl,
  className,
  message,
}: ClientBrandPreviewProps) {
  const validPrimary = primaryColor && HEX_RE.test(primaryColor) ? primaryColor : null;
  const validAccent = accentColor && HEX_RE.test(accentColor) ? accentColor : null;
  const trimmedLogo = logoUrl?.trim() || null;
  const hasBrand = !!validPrimary || !!validAccent || !!trimmedLogo;

  if (!hasBrand) {
    return null;
  }

  return (
    <div className={cn("border-border flex items-center gap-3 rounded-lg border p-3", className)}>
      {trimmedLogo ? (
        <img
          src={trimmedLogo}
          alt=""
          className="size-9 shrink-0 rounded object-contain"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <div className="flex shrink-0 gap-1">
          {validPrimary && (
            <span
              className="border-border size-5 rounded-full border"
              style={{ backgroundColor: validPrimary }}
            />
          )}
          {validAccent && (
            <span
              className="border-border size-5 rounded-full border"
              style={{ backgroundColor: validAccent }}
            />
          )}
        </div>
      )}
      <p className="text-muted-foreground text-xs">
        {message ?? (
          <>
            Preview of <span className="text-foreground font-medium">{name || "this client"}</span>
            &apos;s brand.
          </>
        )}
      </p>
    </div>
  );
}
